"use client";

import React from "react";
import Link from "next/link";
import { getRecentUserTransfers, type Transfer } from "@/lib/supabase-queries";
import { useAuth } from "@/contexts/AuthContext";

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={`w-3 h-3 ${className || ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={`w-[18px] h-[18px] ${className || ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg
    className={`w-[18px] h-[18px] ${className || ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg
    className={`w-[18px] h-[18px] ${className || ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

interface Transaction {
  id: string;
  status: "completed" | "rejected" | "pending" | "cancelled";
  date: string;
  time: string;
  recipients: {
    name: string;
    initials: string;
    color: string;
  }[];
  amount: string;
  isMultiple?: boolean;
}

// Helper function to convert Supabase Transfer to Transaction
function convertTransferToTransaction(transfer: Transfer): Transaction {
  const createdAt = new Date(transfer.created_at);
  const date = createdAt.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  const time = createdAt.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Get beneficiary name or use a default
  const beneficiaryName = transfer.beneficiary?.name || "Unknown";
  const initials = beneficiaryName
    .split(" ")
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Format amount with currency
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: transfer.from_currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(transfer.total_amount || 0);

  return {
    id: transfer.id,
    status: transfer.status as Transaction["status"],
    date,
    time,
    recipients: [
      {
        name: beneficiaryName,
        initials,
        color: "bg-blue-100",
      },
    ],
    amount,
  };
}

function StatusIcon({ status }: { status: Transaction["status"] }) {
  switch (status) {
    case "completed":
      return <CheckIcon className="text-green-600" />;
    case "rejected":
    case "cancelled":
      return <XIcon className="text-red-600" />;
    case "pending":
      return <ClockIcon className="text-yellow-600" />;
    default:
      return <ClockIcon className="text-gray-600" />;
  }
}

function RecipientsList({
  recipients,
  isMultiple,
}: {
  recipients: Transaction["recipients"];
  isMultiple?: boolean;
}) {
  if (isMultiple) {
    return (
      <div className="flex items-center -space-x-3">
        {recipients.map((recipient, index) => (
          <div
            key={index}
            className="w-8 h-8 border-2 border-white rounded-full flex items-center justify-center bg-gray-100"
          >
            <span
              className={`text-xs font-lato ${recipient.name === "+3" ? "text-white" : "text-gray-600"}`}
            >
              {recipient.initials}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100">
        <span className="text-xs font-lato text-blue-600">
          {recipients[0].initials}
        </span>
      </div>
      <span className="font-lato text-base text-gray-900 truncate max-w-[72px]">
        {recipients[0].name}
      </span>
    </div>
  );
}

export const RecentTransactions: React.FC = () => {
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const { user } = useAuth();

  React.useEffect(() => {
    const loadTransactions = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch the last 6 user transfers from Supabase
        const transfers = await getRecentUserTransfers(user.id);

        // Convert Supabase transfers to Transaction format
        const convertedTransactions = transfers.map(
          convertTransferToTransaction
        );

        setTransactions(convertedTransactions);
      } catch (error) {
        console.error("Error loading transactions:", error);
        // Set empty array on error
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-2xl p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-end mb-4">
        <h2 className="text-xl font-light text-gray-900 font-lato max-w-[200px]">
          Recent Transactions
        </h2>
        <Link
          href="/history"
          className="flex flex-row items-center h-auto p-1 text-blue-600 hover:bg-transparent"
        >
          <span className="text-sm font-lato mr-1">View more</span>
          <ChevronRightIcon />
        </Link>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden">
        {/* Table Header - Hidden on mobile */}
        <div className="hidden md:grid bg-gray-100 px-4 py-4 grid-cols-[auto_1fr_1fr_auto] gap-4">
          <div className="font-lato text-base font-bold text-gray-900">
            Status
          </div>
          <div className="font-lato text-base font-bold text-gray-900">
            Date and time
          </div>
          <div className="font-lato text-base font-bold text-gray-900">
            Recipients
          </div>
          <div className="font-lato text-base font-bold text-gray-900">
            Total amount paid
          </div>
        </div>

        {/* Table Rows */}
        {transactions.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-gray-500 font-lato text-base">
              No recent transactions found
            </div>
          </div>
        ) : (
          transactions.map((transaction, index) => (
            <div
              key={transaction.id}
              className={`px-4 py-4 ${index < transactions.length - 1 ? "border-b border-gray-200" : ""}`}
            >
              {/* Desktop Layout */}
              <div className="hidden md:grid grid-cols-[auto_1fr_1fr_auto] gap-4 items-center">
                {/* Status */}
                <div className="flex justify-center">
                  <div className="w-[18px] h-[18px] flex items-center justify-center">
                    <StatusIcon status={transaction.status} />
                  </div>
                </div>

                {/* Date and Time */}
                <div className="space-y-1">
                  <div className="font-lato text-base text-gray-900">
                    {transaction.date}
                  </div>
                  <div className="font-lato text-base font-light text-gray-900">
                    {transaction.time}
                  </div>
                </div>

                {/* Recipients */}
                <div>
                  <RecipientsList
                    recipients={transaction.recipients}
                    isMultiple={transaction.isMultiple}
                  />
                </div>

                {/* Amount and Action */}
                <div className="flex items-center gap-2">
                  <span className="font-lato text-base text-gray-900">
                    {transaction.amount}
                  </span>
                  <ChevronRightIcon className="text-gray-900" />
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="md:hidden space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={transaction.status} />
                    <div>
                      <div className="font-lato text-sm text-gray-900">
                        {transaction.date}
                      </div>
                      <div className="font-lato text-xs font-light text-gray-500">
                        {transaction.time}
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="text-gray-900" />
                </div>

                <div className="flex items-center justify-between">
                  <RecipientsList
                    recipients={transaction.recipients}
                    isMultiple={transaction.isMultiple}
                  />
                  <span className="font-lato text-sm text-gray-900">
                    {transaction.amount}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentTransactions;
